import React, {useEffect, useState} from 'react';
import {NestedKeys} from 'shared/types/key-manipulation';
import {
  translatedProps,
  TranslationKeyProps,
} from 'shared/util/translated-props';
import Loading from '../loading';

import {
  BaseTable,
  Container,
  EmptyColumn,
  ExportButton,
  HeaderColumn,
  HeaderRow,
  Row,
  TableBody,
  TableHeader,
} from './styles';
import IconButton from '../icon-button';
import {SiMicrosoftexcel} from 'react-icons/si';
import useTableState, {DOCUMENT_TYPE} from './use-table-state';
import {ProjectionProps} from '../api-table';
import {ImFilePdf} from 'react-icons/im';
import TableRow from './components/table-row';

export type RenderColumnProps<T = any> = (
  data: T,
  rowIndex: number,
) => React.ReactNode | string;

export type ColumnProps<T = any> = {
  value?: RenderColumnProps<T>;
  visible?: (data: T) => boolean | boolean;
  permission?: string;
  clamp?: number;
  stopPropagation?: boolean;
};

export type ProjectionOptionProps<T = any> = {
  label?: string;
  value?: NestedKeys<T>;
  column?: ColumnProps<T> | RenderColumnProps<T>;
  stopPropagation?: boolean;
};

export interface ExportOptions {
  pdf?: string;
  xlsx?: string;
}

export interface TableProps<T> {
  data: T[];
  options: (NestedKeys<T> | ProjectionOptionProps<T>)[];
  onRowClick?: (row: T, index: number) => any;
  translatedPropsKey: keyof typeof translatedProps | TranslationKeyProps;
  loading?: boolean;
  exports?: ExportOptions;
  projection?: ProjectionProps<T>;
  draggable?: boolean;
  onDrag?: (fromId: string, toId: string) => void;
}

const Table: <T>(p: TableProps<T>) => React.ReactElement<T> = ({
  onRowClick,
  loading,
  translatedPropsKey,
  data,
  options,
  exports,
  projection,
  draggable,
  onDrag,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const getNestedKey: any = (object: any, key: string) => {
    const [firstKey, ...rest] = key.split('.');

    if (rest.length === 0) return object?.[firstKey];
    return getNestedKey(object[firstKey], rest.join('.'));
  };

  const getHeaderLabel = (option: NestedKeys | ProjectionOptionProps) => {
    if (typeof option === 'string')
      return getNestedKey(
        typeof translatedPropsKey === 'string'
          ? translatedProps[translatedPropsKey]
          : translatedPropsKey,
        option,
      );
    if (option.label) return option.label;
    return getNestedKey(
      typeof translatedPropsKey === 'string'
        ? translatedProps[translatedPropsKey]
        : translatedPropsKey,
      option.value,
    );
  };

  const {handleExportData, loadingExportData} = useTableState({
    url: exports?.xlsx ?? '',
    projection: projection!,
    type: DOCUMENT_TYPE.XLSX,
  });

  const {
    handleExportData: handleExportPdfData,
    loadingExportData: loadingExportPdfData,
  } = useTableState({
    url: exports?.pdf ?? '',
    projection: projection!,
    type: DOCUMENT_TYPE.PDF,
  });

  const reorderRows = async (fromId: string, toId: string) => {
    const reorderRows = [...rows];
    const fromIndex = reorderRows.findIndex((row) => row.id === fromId);
    const toIndex = reorderRows.findIndex((row) => row.id === toId);

    if (
      typeof fromIndex !== 'number' ||
      typeof toIndex !== 'number' ||
      reorderRows.length === 0
    )
      return;

    reorderRows.splice(toIndex, 0, reorderRows.splice(fromIndex, 1)[0]);

    setRows(reorderRows);

    if (onDrag) onDrag(fromId, toId);
  };

  useEffect(() => {
    if (data) setRows(data);
  }, [data]);

  return (
    <Container>
      <ExportButton>
        {exports?.xlsx ? (
          <IconButton
            {...{
              icon: <SiMicrosoftexcel />,
              label: 'Exportar',
              loading: loadingExportData,
              onClick: handleExportData,
            }}
          />
        ) : null}

        {exports?.pdf ? (
          <IconButton
            {...{
              icon: <ImFilePdf />,
              label: 'Exportar',
              loading: loadingExportPdfData,
              onClick: handleExportPdfData,
            }}
          />
        ) : null}
      </ExportButton>
      <BaseTable>
        <TableHeader>
          <HeaderRow>
            {options.map((option, index) => (
              <HeaderColumn key={index}>{getHeaderLabel(option)}</HeaderColumn>
            ))}
          </HeaderRow>
        </TableHeader>
        <TableBody>
          {!loading &&
            rows?.length > 0 &&
            rows.map((row, index) => (
              <TableRow
                key={index}
                {...{
                  data: row,
                  options,
                  reorderRows,
                  translatedPropsKey,
                  onRowClick: (data) => onRowClick && onRowClick(data, index),
                  draggable,
                  rowIndex: index,
                }}
              />
            ))}
          {!loading && data?.length === 0 && (
            <Row>
              <EmptyColumn>Nenhum dado encontrado</EmptyColumn>
            </Row>
          )}
          {loading && (
            <Row>
              <EmptyColumn>
                <Loading />
              </EmptyColumn>
            </Row>
          )}
        </TableBody>
      </BaseTable>
    </Container>
  );
};

export default Table;
